import { LocationDetails } from '../../../API/types/Maps';
import { TripDetails } from '../../../API/types/Trips';
import { Location, Locations } from './types';
import { calculateDistance } from './helpers';
import { getFormattedDate } from '../../../utils/dateUtils';

const DAY_MS = 24 * 60 * 60 * 1000;

// A month of GPS points is already a lot to draw; anything longer is unreadable on a
// single map and expensive to fetch, so the requested range is trimmed to this window
// (keeping its most recent end).
export const ROUTE_HISTORY_MAX_DAYS = 30;

// Upper bound sent to the backend for the range query.
export const ROUTE_HISTORY_POINTS_LIMIT = 20000;

// Points further apart than this in time belong to different trips - drawing a line
// between them would invent a route the vehicle never took. The same threshold decides
// what counts as a stop worth marking on the map.
const SEGMENT_GAP_SEC = 30 * 60;

// Trips that started shortly before the window still cover points inside it, so the trip
// query reaches back this far to keep those points attributed to their trip.
export const ROUTE_HISTORY_TRIPS_LOOKBEHIND_MS = 24 * 60 * 60 * 1000;

// ...same for points far apart in space (device offline while the vehicle kept moving).
const SEGMENT_GAP_METERS = 5000;

// Consecutive points closer than this add nothing at the zoom levels a month of driving
// is viewed at, so they are dropped before rendering.
const MIN_POINT_DISTANCE_METERS = 25;

// Hard cap on rendered vertices, whatever the backend returns.
const MAX_RENDERED_POINTS = 5000;

export type RouteHistoryRange = {
  from: string;
  to: string;
  /** True when the selected period was longer than ROUTE_HISTORY_MAX_DAYS and got trimmed. */
  isClamped: boolean;
};

export type RouteHistoryPoint = {
  position: Location;
  timestampSecEpoch: number;
};

export type RouteHistoryTrip = {
  /** Id to open when the segment is clicked - the merged trip when there is one. */
  detailsId: string;
  isMergedTrip: boolean;
  startAt: string;
  endAt: string | null;
  startLocationName?: string;
  endLocationName?: string;
  distanceInKm?: number;
};

export type RouteHistorySegment = {
  key: string;
  path: Locations;
  /** Undefined for points that fall outside every known trip. */
  trip?: RouteHistoryTrip;
};

export type RouteHistoryStop = {
  key: string;
  position: Location;
  fromSecEpoch: number;
  toSecEpoch: number;
  durationSec: number;
};

export type RouteHistoryPath = {
  segments: RouteHistorySegment[];
  stops: RouteHistoryStop[];
  start?: RouteHistoryPoint;
  end?: RouteHistoryPoint;
  pointCount: number;
};

export const EMPTY_ROUTE_HISTORY_PATH: RouteHistoryPath = { segments: [], stops: [], pointCount: 0 };

/**
 * Resolves the page date filter into the window the path is drawn for: it ends at the
 * latest moment of the selected period (never in the future) and spans at most
 * ROUTE_HISTORY_MAX_DAYS back from there.
 */
export const getRouteHistoryRange = (
  startDate?: string,
  endDate?: string,
  now: number = Date.now(),
): RouteHistoryRange | null => {
  const selectedEnd = endDate ? new Date(endDate).getTime() : now;
  if (Number.isNaN(selectedEnd)) return null;
  const toTime = Math.min(selectedEnd, now);

  const selectedStart = startDate ? new Date(startDate).getTime() : NaN;
  const maxWindowStart = toTime - ROUTE_HISTORY_MAX_DAYS * DAY_MS;
  const isClamped = Number.isNaN(selectedStart) || selectedStart < maxWindowStart;
  const fromTime = isClamped ? maxWindowStart : selectedStart;

  if (fromTime >= toTime) return null;

  return {
    from: new Date(fromTime).toISOString(),
    to: new Date(toTime).toISOString(),
    isClamped,
  };
};

export const getRouteHistoryTitle = (range: RouteHistoryRange | null, isOn: boolean): string => {
  if (!isOn) return 'Show route history';
  if (!range) return 'Route history: no period selected';
  const period = `${getFormattedDate(range.from)} - ${getFormattedDate(range.to)}`;
  return range.isClamped
    ? `Route history: ${period} (limited to the last ${ROUTE_HISTORY_MAX_DAYS} days)`
    : `Route history: ${period}`;
};

type OrderedPoint = {
  position: Location;
  timestampSecEpoch: number;
};

const toOrderedPoints = (points: LocationDetails[]): OrderedPoint[] => points
  .filter((point) => {
    const [lng, lat] = point?.location?.coordinates || [];
    return Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);
  })
  .map((point) => ({
    position: [point.location.coordinates[1], point.location.coordinates[0]] as Location,
    timestampSecEpoch: point.timestampSecEpoch,
  }))
  .sort((a, b) => a.timestampSecEpoch - b.timestampSecEpoch);

type TripWindow = RouteHistoryTrip & { startMs: number; endMs: number };

const toTripWindows = (trips?: TripDetails[]): TripWindow[] => (trips || [])
  .filter((trip) => !!trip?._id && !!trip?.startAt)
  .map((trip) => ({
    detailsId: (trip.isMergedTrip && trip.mergedTripId) ? trip.mergedTripId : trip._id,
    isMergedTrip: !!trip.isMergedTrip,
    startAt: trip.startAt,
    endAt: trip.endAt ?? null,
    startLocationName: trip.startLocationName,
    endLocationName: trip.endLocationName,
    distanceInKm: trip.distanceInKm,
    startMs: new Date(trip.startAt).getTime(),
    // An ongoing trip has no end yet, so every later point still belongs to it.
    endMs: trip.endAt ? new Date(trip.endAt).getTime() : Number.POSITIVE_INFINITY,
  }))
  .filter((trip) => Number.isFinite(trip.startMs))
  .sort((a, b) => a.startMs - b.startMs);

// Latest trip that had already started and had not ended at that moment. Windows are sorted,
// so the scan can stop at the first trip starting later.
const findTrip = (windows: TripWindow[], timestampSecEpoch: number): TripWindow | undefined => {
  const ms = timestampSecEpoch * 1000;
  let match: TripWindow | undefined;
  windows.some((window) => {
    if (window.startMs > ms) return true;
    if (ms <= window.endMs) match = window;
    return false;
  });
  return match;
};

const isStop = (previous: OrderedPoint, current: OrderedPoint): boolean => (
  current.timestampSecEpoch - previous.timestampSecEpoch >= SEGMENT_GAP_SEC
);

const jumpedTooFar = (previous: OrderedPoint, current: OrderedPoint): boolean => (
  calculateDistance(previous.position, current.position) > SEGMENT_GAP_METERS
);

type DraftSegment = {
  path: Locations;
  trip?: TripWindow;
};

// Keeps every n-th vertex (plus both ends) so a very long path stays renderable.
const applyStride = (segments: DraftSegment[], stride: number): DraftSegment[] => segments
  .map((segment) => ({
    ...segment,
    path: segment.path.filter((_, idx) => idx % stride === 0 || idx === segment.path.length - 1),
  }))
  .filter((segment) => segment.path.length > 1);

const toSegment = (segment: DraftSegment, idx: number): RouteHistorySegment => ({
  key: `route-history-${idx}-${segment.trip?.detailsId ?? 'no-trip'}-${segment.path[0][0]}`,
  path: segment.path,
  trip: segment.trip && {
    detailsId: segment.trip.detailsId,
    isMergedTrip: segment.trip.isMergedTrip,
    startAt: segment.trip.startAt,
    endAt: segment.trip.endAt,
    startLocationName: segment.trip.startLocationName,
    endLocationName: segment.trip.endLocationName,
    distanceInKm: segment.trip.distanceInKm,
  },
});

/**
 * Turns the raw GPS points of the selected window into polyline segments: chronological,
 * thinned out, and split per trip and wherever the vehicle stood still (or the device was
 * offline), so the map never shows a straight line between two unrelated trips. Each break
 * caused by standing still also yields a stop marker.
 */
export const buildRouteHistoryPath = (
  points?: LocationDetails[],
  trips?: TripDetails[],
): RouteHistoryPath => {
  if (!Array.isArray(points) || !points.length) return EMPTY_ROUTE_HISTORY_PATH;

  const ordered = toOrderedPoints(points);
  if (!ordered.length) return EMPTY_ROUTE_HISTORY_PATH;

  const tripWindows = toTripWindows(trips);
  const drafts: DraftSegment[] = [];
  const stops: RouteHistoryStop[] = [];
  let current: Locations = [];
  let currentTrip: TripWindow | undefined;
  let lastKept: Location | undefined;

  const closeSegment = (lastOfSegment: Location): void => {
    if (lastKept && lastKept !== lastOfSegment) current.push(lastOfSegment);
    if (current.length > 1) drafts.push({ path: current, trip: currentTrip });
    current = [];
    lastKept = undefined;
  };

  ordered.forEach((point, idx) => {
    const previous = idx > 0 ? ordered[idx - 1] : undefined;
    const trip = tripWindows.length ? findTrip(tripWindows, point.timestampSecEpoch) : undefined;

    if (previous) {
      const stopped = isStop(previous, point);
      if (stopped) {
        stops.push({
          key: `stop-${previous.timestampSecEpoch}-${point.timestampSecEpoch}`,
          position: previous.position,
          fromSecEpoch: previous.timestampSecEpoch,
          toSecEpoch: point.timestampSecEpoch,
          durationSec: point.timestampSecEpoch - previous.timestampSecEpoch,
        });
      }
      if (stopped || jumpedTooFar(previous, point) || trip?.detailsId !== currentTrip?.detailsId) {
        closeSegment(previous.position);
      }
    }

    currentTrip = trip;
    if (!lastKept || calculateDistance(lastKept, point.position) >= MIN_POINT_DISTANCE_METERS) {
      current.push(point.position);
      lastKept = point.position;
    }
  });
  closeSegment(ordered[ordered.length - 1].position);

  const totalPoints = drafts.reduce((sum, segment) => sum + segment.path.length, 0);
  const stride = totalPoints > MAX_RENDERED_POINTS ? Math.ceil(totalPoints / MAX_RENDERED_POINTS) : 1;
  const strided = stride > 1 ? applyStride(drafts, stride) : drafts;

  const first = ordered[0];
  const last = ordered[ordered.length - 1];

  return {
    segments: strided.map(toSegment),
    stops,
    start: { position: first.position, timestampSecEpoch: first.timestampSecEpoch },
    end: { position: last.position, timestampSecEpoch: last.timestampSecEpoch },
    pointCount: strided.reduce((sum, segment) => sum + segment.path.length, 0),
  };
};
